import React, { useState } from 'react'
import { ChevronDown, ChevronRight, Info, Plus, X } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatVariableToken } from '@/lib/textTemplateVariables'
import { validateVariableKey } from '@/lib/templateVariableHelpers'

export default function TemplateVariablesTab({
  catalog = [],
  customVariables = [],
  onAddCustom,
  onRemoveCustom,
  legacy = false,
  isSaving = false,
  canPersist = false,
}) {
  const [systemExpanded, setSystemExpanded] = useState(false)
  const [newKey, setNewKey] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [newExample, setNewExample] = useState('')
  const [formError, setFormError] = useState('')

  const formatToken = (key) => {
    if (legacy) {
      return `{{ $${key} }}`
    }
    return formatVariableToken(key)
  }

  const handleAdd = async () => {
    setFormError('')

    const validation = validateVariableKey(newKey, legacy)
    if (!validation.valid) {
      setFormError(validation.error)
      return
    }

    const key = validation.key
    const label = newLabel.trim() || key.replace(/[._>]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

    if (catalog.some((v) => v.key === key)) {
      setFormError('This key already exists in system variables.')
      return
    }

    if (customVariables.some((v) => v.key === key)) {
      setFormError('This custom variable already exists.')
      return
    }

    try {
      await onAddCustom({
        key,
        label,
        type: 'string',
        group: 'custom',
        example: newExample.trim(),
        custom: true,
      })

      setNewKey('')
      setNewLabel('')
      setNewExample('')
    } catch {
      // Parent handles error feedback.
    }
  }

  return (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Define custom variables here for this template. Insert variables into your message using the
          Insert Variable button on the compose tab.
          {!canPersist && ' Custom variables are saved when you save the template.'}
        </AlertDescription>
      </Alert>

      <div className="space-y-3">
        <Label>Custom Variables</Label>
        <div className="space-y-3 rounded-lg border p-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="custom-var-key" className="text-xs">Key</Label>
              <Input
                id="custom-var-key"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder={legacy ? 'custom->field' : 'workflow.notes'}
                className="h-8 font-mono text-sm"
                disabled={isSaving}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="custom-var-label" className="text-xs">Label</Label>
              <Input
                id="custom-var-label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Display name"
                className="h-8 text-sm"
                disabled={isSaving}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="custom-var-example" className="text-xs">Example (optional)</Label>
              <Input
                id="custom-var-example"
                value={newExample}
                onChange={(e) => setNewExample(e.target.value)}
                placeholder="Sample value for preview"
                className="h-8 text-sm"
                disabled={isSaving}
              />
            </div>
          </div>
          {newKey.trim() && (
            <p className="text-xs text-muted-foreground">
              Token preview: <code className="bg-muted px-1 rounded">{formatToken(newKey.trim())}</code>
            </p>
          )}
          {formError && <p className="text-xs text-destructive">{formError}</p>}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={handleAdd}
            disabled={isSaving}
          >
            <Plus className="h-3 w-3 mr-1" />
            {isSaving ? 'Saving...' : 'Add Custom Variable'}
          </Button>
        </div>

        {customVariables.length > 0 ? (
          <div className="grid gap-2">
            {customVariables.map((variable) => (
              <div key={variable.key} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="text-sm bg-muted px-2 py-1 rounded break-all">{formatToken(variable.key)}</code>
                    <span className="text-sm font-medium">{variable.label}</span>
                  </div>
                  {variable.example && (
                    <p className="text-xs text-muted-foreground mt-1">Example: {variable.example}</p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveCustom(variable.key)}
                  disabled={isSaving}
                  aria-label={`Remove ${variable.key}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No custom variables yet.</p>
        )}
      </div>

      <div className="space-y-2">
        <button
          type="button"
          className="flex items-center gap-2 text-sm font-medium hover:text-foreground text-muted-foreground"
          onClick={() => setSystemExpanded((prev) => !prev)}
        >
          {systemExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Browse system variables ({catalog.length})
        </button>
        {systemExpanded && (
          <div className="grid gap-2 max-h-64 overflow-y-auto border rounded-lg p-2">
            {catalog.map((variable) => (
              <div key={variable.key} className="p-3 border rounded-lg bg-muted/20">
                <div className="flex flex-wrap items-center gap-2">
                  <code className="text-sm bg-muted px-2 py-1 rounded break-all">{formatToken(variable.key)}</code>
                  <span className="text-sm font-medium">{variable.label || variable.description}</span>
                </div>
                {variable.example && (
                  <p className="text-xs text-muted-foreground mt-1">Example: {variable.example}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
